import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { parseLocalDate } from '../../../utils/dateUtils';

const statusConfig = {
  'Assigned': { color: 'text-blue-600', dot: 'bg-blue-500', label: 'Assigned' },
  'Available': { color: 'text-green-600', dot: 'bg-green-500', label: 'Available' },
  'Maintenance': { color: 'text-yellow-600', dot: 'bg-yellow-500', label: 'Maintenance' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return parseLocalDate(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const ResourceDetailModal = ({ resource, onClose, onEdit, onDelete, onUpdateStatus }) => {
  if (!resource) return null;

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${resource?.name}? This action cannot be undone.`)) {
      onDelete?.(resource?.id);
    }
  };

  const handleToggleMaintenance = () => {
    const newStatus = resource?.status === 'Maintenance' ? 'Available' : 'Maintenance';
    onUpdateStatus?.(resource?.id, { status: newStatus });
  };

  const sc = statusConfig?.[resource?.status] || statusConfig?.['Available'];

  // Compute date window: 30 days before today to 90 days after today
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  const windowStart = new Date(today);
  windowStart?.setDate(windowStart?.getDate() - 30);
  const windowEnd = new Date(today);
  windowEnd?.setDate(windowEnd?.getDate() + 90);

  const rangeLabel = useMemo(() => {
    const fmt = (d) => d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(windowStart)} – ${fmt(windowEnd)}`;
  }, []);

  // Filter assignments within the date window
  const windowAssignments = useMemo(() => {
    const allAssignments = resource?.assignments || [];
    return allAssignments?.filter((a) => {
      const startRaw = a?.startDate || a?.start_date;
      const endRaw = a?.endDate || a?.end_date;
      if (!startRaw || !endRaw) return false;
      const start = parseLocalDate(startRaw);
      const end = parseLocalDate(endRaw);
      // Overlaps the window if start <= windowEnd AND end >= windowStart
      return start <= windowEnd && end >= windowStart;
    })?.sort((a, b) => {
      const aStart = parseLocalDate(a?.startDate || a?.start_date || null) || new Date(0);
      const bStart = parseLocalDate(b?.startDate || b?.start_date || null) || new Date(0);
      return aStart - bStart;
    });
  }, [resource?.assignments]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            Resource Details
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-1 capitalize">
                {resource?.name}
              </h3>
              {resource?.identifier && (
                <p className="text-sm text-muted-foreground">ID: {resource?.identifier}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Category</p>
                <p className="text-sm text-foreground">{resource?.category}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${sc?.dot}`} />
                  <span className={`text-sm font-medium ${sc?.color}`}>{sc?.label}</span>
                  {resource?.status === 'Assigned' && (
                    <span className="text-xs text-muted-foreground">(auto — assignment in next 30 days)</span>
                  )}
                  {resource?.status === 'Available' && (
                    <span className="text-xs text-muted-foreground">(auto — no upcoming assignment)</span>
                  )}
                </div>
              </div>
            </div>

            {/* Maintenance toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
              <Icon name="Wrench" size={18} className="text-yellow-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Maintenance Mode</p>
                <p className="text-xs text-muted-foreground">
                  {resource?.status === 'Maintenance' ?'This resource is currently under maintenance and unavailable for assignment.' :'Mark this resource as under maintenance to prevent assignment.'}
                </p>
              </div>
              <Button
                variant={resource?.status === 'Maintenance' ? 'outline' : 'default'}
                size="sm"
                onClick={handleToggleMaintenance}
                iconName={resource?.status === 'Maintenance' ? 'CheckCircle2' : 'Wrench'}
                iconPosition="left"
                iconSize={14}
              >
                {resource?.status === 'Maintenance' ? 'Mark Available' : 'Set Maintenance'}
              </Button>
            </div>

            {/* Assignments within date window */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Assignments
              </p>
              {windowAssignments?.length > 0 ? (
                <div className="rounded-lg border border-border overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-muted/50 border-b border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Project</p>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Phase</p>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Deliverable</p>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dates</p>
                  </div>
                  {/* Rows */}
                  <div className="divide-y divide-border">
                    {windowAssignments?.map((a, idx) => {
                      const startRaw = a?.startDate || a?.start_date;
                      const endRaw = a?.endDate || a?.end_date;
                      const projectName = a?.projectName || a?.project_name || '—';
                      const phaseName = a?.phaseName || a?.phase_name || '—';
                      const deliverableName = a?.deliverableName || a?.deliverable_name || (a?.source === 'deliverable' ? phaseName : '—');
                      const phaseDisplay = a?.source === 'deliverable' ? '—' : phaseName;
                      const deliverableDisplay = a?.source === 'deliverable' ? phaseName : deliverableName;

                      return (
                        <div key={a?.id || idx} className="grid grid-cols-4 gap-2 px-3 py-2.5 hover:bg-muted/30 transition-colors">
                          <p className="text-xs text-foreground font-medium truncate" title={projectName}>{projectName}</p>
                          <p className="text-xs text-muted-foreground truncate" title={phaseDisplay}>{phaseDisplay}</p>
                          <p className="text-xs text-muted-foreground truncate" title={deliverableDisplay}>{deliverableDisplay}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(startRaw)}<br />{formatDate(endRaw)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 py-4 px-3 rounded-lg bg-muted/30 border border-border">
                  <Icon name="CalendarOff" size={16} className="text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No assignments in this date range.</p>
                </div>
              )}
              {/* Footnote */}
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Icon name="Info" size={12} className="shrink-0" />
                Showing assignments from <span className="font-medium">{rangeLabel}</span> (30 days before today through 90 days after today).
              </p>
            </div>

            {resource?.specifications && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Specifications
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  {Object.entries(resource?.specifications)?.map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="text-muted-foreground capitalize">
                        {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                      </span>
                      <span className="text-foreground font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resource?.utilizationHistory && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Utilization History
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Total Projects</p>
                      <p className="text-xl md:text-2xl font-semibold text-foreground">
                        {resource?.utilizationHistory?.totalProjects}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Total Hours</p>
                      <p className="text-xl md:text-2xl font-semibold text-foreground">
                        {resource?.utilizationHistory?.totalHours}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Utilization Rate</p>
                      <p className="text-xl md:text-2xl font-semibold text-success">
                        {resource?.utilizationHistory?.utilizationRate}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-4 md:px-6 py-4 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="destructive" iconName="Trash2" iconPosition="left" onClick={handleDelete}>
            Delete
          </Button>
          <Button variant="default" iconName="Edit" iconPosition="left" onClick={() => onEdit(resource)}>
            Edit Resource
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetailModal;